<script setup lang="ts">
import { TabsIndicator, TabsList, TabsRoot, TabsTrigger } from 'reka-ui'

definePageMeta({
  layout: 'nettbutikk-dark',
  theme: 'blurple',
  colorMode: 'light',
})

const { data, suspense } = useArticles('/om-juridika')

await suspense()

const route = useRoute('about-slug')

const navItems = computed(() => {
  let selected: CrystallizeArticle | undefined

  const articles: CrystallizeArticle[] = []

  // Used in dropdown on mobile, contains both articles and links
  interface Option { value: string, label: string | null | undefined }

  const options: Option[] = []

  const children = data.value?.children ?? []
  for (const article of children) {
    const option = { value: article.id, label: article.components.title }

    if (isHighlighted(article)) {
      options.unshift(option)
      articles.unshift(article)
      selected ??= article
    }
    else {
      options.push(option)
      articles.push(article)

      if (getSlug(article) === route.params.slug) {
        selected = article
      }
    }
  }

  const links = data.value?.components?.links ?? []
  for (const link of links) {
    options.push({ value: link.url, label: link.label })
  }

  return {
    selected,
    options,
    articles,
  }
})

function isHighlighted(article: CrystallizeArticle) {
  return article.id === data.value?.components?.highlighted?.id
}

const selectedTab = computed(() => navItems.value.selected?.id)

const listClass = 'flex flex-col gap-1 sm:gap-2.5'

const linkClass = 'no-underline px-1 sm:px-3 py-2 sm:py-4 inline-block'

function handleFormSelect(idOrUrl: string | undefined) {
  if (!idOrUrl) {
    return
  }

  const article = navItems.value.articles.find(a => a.id === idOrUrl)
  if (!article) {
    return navigateTo(idOrUrl)
  }

  if (isHighlighted(article)) {
    return navigateTo({ name: 'about-index' })
  }

  return navigateTo({
    name: 'about-slug',
    params: { slug: getSlug(article) },
  })
}
</script>

<template>
  <ContentGrid class="text-white mt-m lg:mt-xl mb-2xl">
    <div class="full-width-content grid grid-cols-1 lg:grid-cols-[250px_1fr] grid-rows-[auto_1fr] gap-x-xl gap-y-m lg:gap-y-xl">
      <aside :class="listClass" class="hidden lg:flex row-start-2">
        <TabsRoot v-model="selectedTab" orientation="vertical" activation-mode="manual">
          <TabsList class="relative" :class="listClass">
            <TabsIndicator class="absolute transition-transform w-full rounded-lg bg-blurple-100 text-primary z-0 h-[--reka-tabs-indicator-size] translate-y-[--reka-tabs-indicator-position]" />
            <TabsTrigger
              v-for="article in navItems.articles"
              :key="article.id"
              :value="article.id"
              as-child
              :class="linkClass"
              class="rounded-lg data-[state=active]:text-primary transition-colors z-[1]"
            >
              <NuxtLink :to="isHighlighted(article) ? { name: 'about-index' } : { name: 'about-slug', params: { slug: getSlug(article) } }">
                {{ article.components.title }}
              </NuxtLink>
            </TabsTrigger>
          </TabsList>
        </TabsRoot>

        <template v-if="data?.components?.links?.length">
          <hr class="bg-[#DEDEDE] h-px">
          <ul :class="listClass">
            <li v-for="link in data.components.links" :key="link.url">
              <NuxtLink :to="link.url" :class="linkClass">
                {{ link.label }}
              </NuxtLink>
            </li>
          </ul>
        </template>
      </aside>
      <aside v-if="navItems.options.length" class="lg:hidden text-primary">
        <FormSelect intent="filled" :model-value="selectedTab" :options="navItems.options" @update:model-value="handleFormSelect">
          <template #option="{ option }">
            {{ option.label }}
          </template>
        </FormSelect>
      </aside>

      <article class="row-span-2 grid grid-rows-subgrid">
        <NuxtPage :article="navItems.selected" />
      </article>
    </div>
  </ContentGrid>
</template>
